import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { ORDER_STATUSES } from '../../utils/statuses';
import { fmt, money, errMsg } from '../../utils/helpers';
import { Badge, Spinner, EmptyState, Select } from '../../components/common/UI';
import { NewOrderModal } from '../NewEntityModals';
import { Plus, Search, RefreshCw, FileText, CheckCircle, AlertCircle, Clock, Truck, Info } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_OPTS = [{ value: '', label: 'Все активные' },
  ...Object.entries(ORDER_STATUSES).map(([v, d]) => ({ value: v, label: d.label }))];

// Значок статуса счёта
function InvoiceBadge({ hasInvoice }) {
  if (!hasInvoice) return (
    <span title="Счёт не выставлен" className="text-gray-600"><FileText size={14} /></span>
  );
  return (
    <span title="Счёт выставлен" className="text-emerald-400"><FileText size={14} /></span>
  );
}

function ShipmentBadge({ order }) {
  const ordered = Number(order?.ordered_qty_total || 0);
  const shipped = Number(order?.shipped_qty_total || 0);
  if (ordered <= 0 || shipped <= 0) return <Badge className="bg-gray-800 text-gray-400 border-gray-700">Не отгружен</Badge>;
  if (shipped >= ordered) return <Badge className="bg-green-900/50 text-green-300 border-green-700">Полностью отгружен</Badge>;
  return <Badge className="bg-amber-900/50 text-amber-300 border-amber-700">Частично отгружен</Badge>;
}

function shipmentDeadlineMeta(order) {
  const ordered = Number(order?.ordered_qty_total || 0);
  const shipped = Number(order?.shipped_qty_total || 0);
  const isFullyShipped = ordered > 0 && shipped >= ordered;
  if (!isFullyShipped || !order?.last_shipment_at) return null;

  const shippedAt = new Date(order.last_shipment_at);
  if (Number.isNaN(shippedAt.getTime())) return null;

  if (!order?.deadline) {
    return { label: fmt(order.last_shipment_at), className: 'text-gray-400', hint: 'Дата последней отгрузки' };
  }

  const deadline = new Date(order.deadline);
  deadline.setHours(23, 59, 59, 999);
  const diffMs = shippedAt.getTime() - deadline.getTime();
  const diffDays = diffMs / 86400000;

  if (diffDays <= 0) {
    return { label: fmt(order.last_shipment_at), className: 'text-emerald-400 font-medium', hint: 'Последняя отгрузка в срок' };
  }
  if (diffDays <= 7) {
    return { label: fmt(order.last_shipment_at), className: 'text-amber-300 font-medium', hint: 'Последняя отгрузка с просрочкой до 7 дней' };
  }
  return { label: fmt(order.last_shipment_at), className: 'text-red-400 font-medium', hint: 'Последняя отгрузка с просрочкой более 7 дней' };
}

function PaymentStatusBlock({ paid, total, showPrices }) {
  if (!showPrices) return null;

  const paidAmount = Number(paid || 0);
  const totalAmount = Number(total || 0);
  const debtAmount = Math.max(totalAmount - paidAmount, 0);

  if (paidAmount <= 0) {
    return (
      <div className="space-y-0.5 text-right">
        <div className="text-xs font-medium text-amber-300">Не оплачено</div>
        <div className="mono text-xs text-red-300">{money(debtAmount)}</div>
      </div>
    );
  }

  if (paidAmount >= totalAmount && totalAmount > 0) {
    return (
      <div className="space-y-0.5 text-right">
        <div className="mono text-xs text-amber-300">{money(paidAmount)}</div>
        <div className="text-xs font-medium text-emerald-300">Оплачено</div>
      </div>
    );
  }

  return (
    <div className="space-y-0.5 text-right">
      <div className="mono text-xs text-amber-300">{money(paidAmount)}</div>
      <div className="mono text-xs text-red-300">{money(debtAmount)}</div>
    </div>
  );
}


function HeaderHint({ label, hint, lines, align = 'left' }) {
  const alignCls = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : '';
  const popupAlignCls = align === 'right'
    ? 'right-0'
    : align === 'center'
      ? 'left-1/2 -translate-x-1/2'
      : 'left-0';

  return (
    <span className={`relative inline-flex ${alignCls}`}>
      <span className="group inline-flex items-center gap-1 cursor-help">
        <span>{label}</span>
        <Info size={12} className="text-gray-600" />
        <span className={`pointer-events-none invisible absolute top-full z-30 mt-2 w-max max-w-[320px] rounded-xl border border-gray-800 bg-gray-950/100 ring-1 ring-black/70 px-3 py-2 text-left shadow-2xl shadow-black/60 transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100 opacity-0 translate-y-1 ${popupAlignCls}`}>
          {Array.isArray(lines) && lines.length > 0 ? (
            <span className="flex flex-col gap-1">
              {lines.map((line, idx) => (
                <span key={`${label}-${idx}`} className={line.className || 'text-gray-300'}>
                  {line.text}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-gray-300">{hint}</span>
          )}
        </span>
      </span>
    </span>
  );
}

export default function OrdersPage() {
  const { hasPerm, isProducer, isDealerAdmin } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const LIMIT = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/orders', { params: { search, status, page, limit: LIMIT } });
      setOrders(data.data);
      setTotal(data.total);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  }, [search, status, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, status]);

  const showPrices = hasPerm('orders', 'view_prices');
  const showFullFinance = (isProducer || isDealerAdmin) && showPrices;
  const showPaymentStatus = showFullFinance;

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-100">Заказы</h1>
          <p className="text-xs text-gray-600 mt-0.5">{total} записей</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost p-2" onClick={load} title="Обновить">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
          {hasPerm('orders', 'create') && (
            <button className="btn-primary" onClick={() => setShowNew(true)}>
              <Plus size={15} /> Новый заказ
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input className="input pl-8" placeholder="Поиск по номеру, названию..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="w-44">
          <Select value={status} onChange={setStatus} options={STATUS_OPTS} />
        </div>
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(ORDER_STATUSES).map(([k, v]) => (
          <button key={k} onClick={() => setStatus(status === k ? '' : k)}
            className={'badge border cursor-pointer transition-all ' + (status === k ? v.color : 'bg-transparent text-gray-500 border-gray-700 hover:border-gray-500')}>
            {v.label}
          </button>
        ))}
      </div>

      <div className="card overflow-hidden">
        {loading && orders.length === 0
          ? <div className="flex justify-center py-12"><Spinner size={24} /></div>
          : orders.length === 0 ? <EmptyState text="Заказы не найдены" />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-28">Номер</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Название</th>
                    {isProducer && <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium">Дилер</th>}
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-32">Статус</th>
                    <th className="text-center px-4 py-3 text-xs text-gray-500 font-medium w-16">%</th>
                    {showPrices && (
                      <th className="text-right px-4 py-3 text-xs text-gray-500 font-medium w-40">
                        {showFullFinance
                          ? (
                            <HeaderHint
                              align="right"
                              label="Стоимость"
                              lines={[
                                { text: 'Цена заказа — стоимость для клиента.', className: 'text-gray-200' },
                                { text: 'Цена производителя — себестоимость дилера.', className: 'text-cyan-300' },
                                { text: 'Маржа дилера — что остаётся дилеру.', className: 'text-emerald-300' },
                              ]}
                            />
                          )
                          : <HeaderHint align="right" label="Стоимость" hint={isProducer ? 'Итоговая стоимость заказа по расчёту системы.' : 'Стоимость заказа в доступной для вас цене.'} />}
                      </th>
                    )}
                    {showPrices && (
                      <th className="text-right px-4 py-3 text-xs text-gray-500 font-medium w-36">
                        <HeaderHint
                          align="right"
                          label="Отгружено"
                          lines={showFullFinance
                            ? [
                                { text: 'Цена производителя отгруженного товара.', className: 'text-cyan-300' },
                              ]
                            : [
                                { text: 'Стоимость уже отгруженного товара в доступной для вас цене.', className: 'text-gray-300' },
                              ]}
                        />
                      </th>
                    )}
                    <th className="text-center px-4 py-3 text-xs text-gray-500 font-medium w-40">Статус отгрузки</th>
                    {showPaymentStatus && (
                      <th className="text-center px-4 py-3 text-xs text-gray-500 font-medium w-44">
                        <HeaderHint
                          align="center"
                          label="Статус оплаты"
                          lines={[
                            { text: 'Жёлтая верхняя строка — оплачено или статус «Не оплачено».', className: 'text-amber-300' },
                            { text: 'Красная нижняя строка — остаток долга по заказу.', className: 'text-red-300' },
                            { text: 'Зелёная нижняя строка — заказ оплачен полностью.', className: 'text-emerald-300' },
                          ]}
                        />
                      </th>
                    )}
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-28">Создан</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 font-medium w-32">
                        <HeaderHint
                          align="right"
                          label="Дедлайн"
                          lines={[
                            { text: 'Верхняя дата — плановый дедлайн заказа.', className: 'text-gray-200' },
                            { text: 'Зелёная дата ниже — последняя отгрузка в срок.', className: 'text-emerald-300' },
                            { text: 'Оранжевая дата ниже — последняя отгрузка с просрочкой до 7 дней.', className: 'text-amber-300' },
                            { text: 'Красная дата ниже — последняя отгрузка с просрочкой более 7 дней.', className: 'text-red-300' },
                          ]}
                        />
                      </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => {
                    const st = ORDER_STATUSES[o.status] || {};
                    const overdue = o.deadline && new Date(o.deadline) < new Date() && !['shipped','archived'].includes(o.status);
                    const retailTotal = Number(o.price_retail || 0);
                    const dealerTotal = Number(o.price_dealer || 0);
                    const displayTotal = showFullFinance ? dealerTotal : retailTotal;
                    const marginTotal = Math.max(retailTotal - dealerTotal, 0);
                    const displayShipped = showFullFinance ? Number(o.shipped_dealer_total || 0) : Number(o.shipped_retail_total || 0);
                    const shipmentMeta = shipmentDeadlineMeta(o);
                    return (
                      <tr key={o.id} className="table-row" onClick={() => navigate('/orders/' + o.id)}>
                        <td className="px-4 py-3">
                          <span className="mono text-blue-400 text-xs">{o.order_number}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-1">
                            <span className="text-gray-200 font-medium">{o.title}</span>
                            {o.has_unreceived_shipments && (
                              <div className="flex items-center gap-1.5 text-[11px] text-amber-300">
                                <Truck size={12} className="text-amber-400" />
                                <span>Отгружено, не получено{(o.unreceived_shipments_count || 0) > 1 ? ` · ${o.unreceived_shipments_count}` : ''}</span>
                              </div>
                            )}
                          </div>
                        </td>
                        {isProducer && <td className="px-4 py-3 text-gray-400">{o.dealer_name}</td>}
                        <td className="px-4 py-3">
                          <Badge className={st.color}>{st.label}</Badge>
                        </td>
                        {/* Только % без прогрессбара */}
                        <td className="px-4 py-3 text-center">
                          <span className="mono text-xs text-gray-400">{o.progress}%</span>
                        </td>
                        {showPrices && (
                          <td className="px-4 py-3 text-right">
                            {showFullFinance ? (
                              <div className="space-y-0.5">
                                <div className="mono text-gray-200" title="Стоимость заказа для клиента">{money(retailTotal)}</div>
                                <div className="mono text-cyan-300 text-xs" title="Себестоимость для дилера">{money(dealerTotal)}</div>
                                <div className="mono text-emerald-300 text-xs" title="Маржа дилера">{money(marginTotal)}</div>
                              </div>
                            ) : (
                              <span className="mono text-gray-300">{money(displayTotal)}</span>
                            )}
                          </td>
                        )}
                        {showPrices && (
                          <td className="px-4 py-3 text-right">
                            <span className="mono text-gray-300">{money(displayShipped)}</span>
                          </td>
                        )}
                        <td className="px-4 py-3 text-center">
                          <ShipmentBadge order={o} />
                        </td>
                        {showPaymentStatus && (
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <InvoiceBadge hasInvoice={o.has_invoice} />
                              <PaymentStatusBlock paid={o.paid_total} total={o.price_dealer} showPrices={showPaymentStatus} />
                            </div>
                          </td>
                        )}
                        <td className="px-4 py-3 text-xs text-gray-600">{fmt(o.created_at)}</td>
                        <td className="px-4 py-3 text-xs">
                          <div className="space-y-0.5">
                            <div className={overdue ? 'text-red-400 font-medium' : 'text-gray-500'}>
                              {fmt(o.deadline) || '—'}{overdue && ' ⚠'}
                            </div>
                            {shipmentMeta && (
                              <div className={`flex items-center gap-1 ${shipmentMeta.className}`} title={shipmentMeta.hint}>
                                <Truck size={11} className="opacity-80" />
                                <span>{shipmentMeta.label}</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {total > LIMIT && (
        <div className="flex items-center justify-center gap-2">
          <button className="btn-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>←</button>
          <span className="text-sm text-gray-500">стр. {page} из {Math.ceil(total / LIMIT)}</span>
          <button className="btn-secondary" disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)}>→</button>
        </div>
      )}

      {showNew && <NewOrderModal onClose={() => { setShowNew(false); load(); }} />}
    </div>
  );
}
